import { hasModifierKey } from '@angular/cdk/keycodes';
import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	EventEmitter,
	inject,
	Injector,
	input,
	NgZone,
	OnDestroy,
	Output,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LvrButton } from '../../../../../core/components/lvr-button/lvr-button';
import { DatabaseFilterPanelRegistry } from './database-filter-panel.registry';

@Component({
	selector: 'lvr-database-multi-select-filter',
	imports: [MatAutocompleteModule, MatIcon, MatTooltipModule, TranslocoPipe, LvrButton],
	templateUrl: './database-multi-select-filter.html',
	styleUrl: './database-multi-select-filter.scss',
	host: {
		style: 'display: contents;',
	},
	encapsulation: ViewEncapsulation.None,
})
export class DatabaseMultiSelectFilter implements OnDestroy {
	private readonly panelRegistry = inject(DatabaseFilterPanelRegistry);
	private readonly injector = inject(Injector);
	private readonly transloco = inject(TranslocoService);
	private readonly zone = inject(NgZone);
	private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
	private trigger: MatAutocompleteTrigger | null = null;

	private readonly autocomplete = viewChild.required(MatAutocomplete);
	private readonly filterInput = viewChild.required<ElementRef<HTMLInputElement>>('filterInput');

	public readonly options = input<readonly string[]>([]);
	/** Display text per option value. Values without an entry are shown as-is. */
	public readonly optionLabels = input<Readonly<Record<string, string>>>({});
	public readonly selectedValues = input<readonly string[]>([]);
	public readonly applying = input(false);
	public readonly disabled = input(false);
	// Kept for backwards compatibility with existing usages; panel width is centrally controlled.
	public readonly panelWidth = input<string | null>(null);

	@Output() public readonly selectionApplied = new EventEmitter<readonly string[]>();
	@Output() public readonly panelClosed = new EventEmitter<void>();
	private readonly searchText = signal('');
	public readonly inputFocused = signal(false);
	private readonly pendingValues = signal<readonly string[]>([]);
	private readonly optionOrderSelection = signal<readonly string[]>([]);

	public constructor() {
		// The trigger input doubles as the search field, so `Space` has to stay a literal space and
		// `Enter` toggles instead. Material's trigger selects the active option on `Enter`, and an
		// option selection is one of its panel closing actions. Listening on the host in the capture
		// phase lets us swallow `Enter` before the trigger's own keydown listener ever sees it, while
		// every other key (arrows, Escape, Tab, typing) reaches Material untouched.
		const onKeydown = (event: KeyboardEvent) => this.toggleActiveOptionOnEnter(event);
		// Bubble phase runs after Material's trigger has moved the active option.
		const onKeydownAfterTrigger = (event: KeyboardEvent) => this.scrollActiveOptionIntoView(event);

		this.hostElement.addEventListener('keydown', onKeydown, true);
		this.hostElement.addEventListener('keydown', onKeydownAfterTrigger);
		inject(DestroyRef).onDestroy(() => {
			this.hostElement.removeEventListener('keydown', onKeydown, true);
			this.hostElement.removeEventListener('keydown', onKeydownAfterTrigger);
		});
	}

	public readonly filteredOptions = computed(() => {
		const term = this.searchText().trim().toLowerCase();
		const selected = new Set(this.optionOrderSelection());
		const options = this.options().filter((option) => !term || this.labelFor(option).toLowerCase().includes(term));
		return options.sort((left, right) => Number(selected.has(right)) - Number(selected.has(left)));
	});

	public labelFor(option: string): string {
		return this.optionLabels()[option] ?? option;
	}

	public readonly emptyAutocompleteDisplay = () => '';

	public readonly selectedCount = computed(() => this.pendingValues().length);

	public readonly displayValue = computed(() => {
		if (this.inputFocused()) return this.searchText();
		const selectedCount = this.selectedValues().length;
		if (selectedCount === 0) return '';
		return this.transloco.translate('database_view.filters.selected_of_total', {
			count: selectedCount,
			total: this.options().length,
		});
	});

	public openPanel(trigger: MatAutocompleteTrigger, event?: Event) {
		event?.stopPropagation();
		if (this.disabled()) return;
		this.trigger = trigger;
		this.panelRegistry.register(this);

		if (!this.inputFocused()) {
			this.pendingValues.set([...this.selectedValues()]);
		}
		this.onInputFocus();

		if (!trigger.panelOpen) {
			trigger.openPanel();
		}
	}

	public onInputFocus() {
		this.inputFocused.set(true);
	}

	public onInputBlur() {
		this.inputFocused.set(false);
		this.searchText.set('');
	}

	public setSearchValue(value: string) {
		this.searchText.set(value);
	}

	public keepInputFocused(event: MouseEvent) {
		event.preventDefault();
	}

	public toggleOption(option: string, event: MouseEvent) {
		event.stopPropagation();
		if (this.isLocked()) return;
		this.togglePendingValue(option);
	}

	public selectAll() {
		if (this.isLocked()) return;
		this.pendingValues.set([...this.options()]);
	}

	public clearAll() {
		if (this.isLocked()) return;
		this.pendingValues.set([]);
	}

	public canSelectAll() {
		return !this.isLocked() && this.pendingValues().length < this.options().length;
	}

	public canClearAll() {
		return !this.isLocked() && this.pendingValues().length > 0;
	}

	public canApply() {
		if (this.isLocked()) return false;
		const pending = new Set(this.pendingValues());
		const selected = new Set(this.selectedValues());
		return pending.size !== selected.size || [...pending].some((value) => !selected.has(value));
	}

	public apply() {
		if (!this.canApply()) return;
		this.selectionApplied.emit([...this.pendingValues()]);
		this.filterInput().nativeElement.blur();
		this.onInputBlur();
	}

	public closePanel() {
		if (this.trigger?.panelOpen) {
			this.trigger.closePanel();
		}
	}

	public containsTarget(target: EventTarget | null) {
		const element = target as HTMLElement | null;
		if (!element) return false;
		const overlayPanel = this.autocomplete().panel?.nativeElement;
		return this.hostElement.contains(element) || (!!overlayPanel && overlayPanel.contains(element));
	}

	public onPanelOpened() {
		this.optionOrderSelection.set([...this.selectedValues()]);
		// Material opens the panel on its own for `ArrowDown`, bypassing `openPanel`, so the registry
		// has to be kept in sync from here as well.
		this.panelRegistry.register(this);

		// `opened` fires while the panel is still being rendered, and Material resets the active option
		// in an `afterNextRender` of its own, so both steps below have to wait for the render to finish.
		afterNextRender(
			() =>
				this.zone.run(() => {
					const autocomplete = this.autocomplete();
					autocomplete.panel?.nativeElement.setAttribute('aria-multiselectable', 'true');

					// Material activates the first option on open; prefer the first already selected one.
					const options = autocomplete.options?.toArray() ?? [];
					const firstSelected = options.findIndex((option) => !option.disabled && this.isOptionSelected(option.value));
					if (firstSelected > -1) {
						autocomplete._keyManager?.setActiveItem(firstSelected);
					}
				}),
			{ injector: this.injector },
		);
	}

	public onPanelClosed() {
		this.panelRegistry.unregister(this);
		this.panelClosed.emit();
	}

	public isOptionSelected(option: string) {
		return this.pendingValues().includes(option);
	}

	public ngOnDestroy() {
		this.panelRegistry.unregister(this);
	}

	private toggleActiveOptionOnEnter(event: KeyboardEvent) {
		if (event.key !== 'Enter' || hasModifierKey(event)) return;

		const autocomplete = this.autocomplete();
		if (!autocomplete.isOpen) return;

		const activeOption = autocomplete._keyManager?.activeItem;
		if (!activeOption || activeOption.disabled) return;

		event.preventDefault();
		event.stopPropagation();
		if (!this.isLocked()) this.togglePendingValue(activeOption.value as string);
	}

	private isLocked() {
		return this.applying() || this.disabled();
	}

	private scrollActiveOptionIntoView(event: KeyboardEvent) {
		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

		const autocomplete = this.autocomplete();
		const list = autocomplete.panel?.nativeElement.querySelector('.multi-select-options') as HTMLElement | null;
		const activeOption = autocomplete._keyManager?.activeItem;
		if (!list || !activeOption) return;

		const listRect = list.getBoundingClientRect();
		const optionRect = activeOption._getHostElement().getBoundingClientRect();
		if (optionRect.top < listRect.top) {
			list.scrollTop -= listRect.top - optionRect.top;
		} else if (optionRect.bottom > listRect.bottom) {
			list.scrollTop += optionRect.bottom - listRect.bottom;
		}
	}

	private togglePendingValue(option: string) {
		this.pendingValues.update((values) =>
			values.includes(option) ? values.filter((value) => value !== option) : [...values, option],
		);
	}
}
